import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import apiService from '../services/api.service';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { StatusDot } from '../components/ui/status-dot';
import { Toolbar } from '../components/ui/page-header';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { DataTable } from '../components/data-table/DataTable';
import { BulkAction, BulkActionBar } from '../components/data-table/BulkActionBar';
import { FilterChip } from '../components/data-table/FilterChip';
import { SearchField } from '../components/data-table/SearchField';
import { STAFF_ROLES, StaffFormValues, StaffMember, StaffPatch, StaffRole, StaffSheet } from '../components/staff/StaffSheet';

// Juges (mode Course, refonte 2026-09-27) : la grammaire de la page Équipes,
// en plus petit — un nom, un email, un rôle. Recherche, tri, filtre par rôle,
// sélection multiple (rôle en lot, retrait), ajout et édition en panneau.

const staffQueryKey = (eventId: string | null) => ['event-staff', eventId] as const;

const EventStaff: React.FC = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { selectedEventId } = useEvent();

  const staffQuery = useQuery({
    queryKey: staffQueryKey(selectedEventId),
    queryFn: async () => ((await apiService.getEventStaff(selectedEventId as string)).staff || []) as StaffMember[],
    enabled: !!selectedEventId,
    staleTime: 15 * 1000,
  });
  const staff = useMemo(() => staffQuery.data ?? [], [staffQuery.data]);
  const refresh = () => qc.invalidateQueries({ queryKey: staffQueryKey(selectedEventId) });
  const roleLabel = (r: string) => t(`eventStaff.role.${r}`);
  const displayName = (s: StaffMember) => s.first_name || s.user_name || s.email;

  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return staff.filter((s) => {
      if (roleFilter.length && !roleFilter.includes(s.role)) return false;
      return !q || [s.first_name, s.user_name, s.email, roleLabel(s.role)].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff, query, roleFilter, t]);

  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const selectedIds = Object.keys(selection).filter((id) => selection[id]);
  const selected = staff.filter((s) => selection[s.id]);
  useEffect(() => { setSelection({}); }, [selectedEventId]);

  const [sheetId, setSheetId] = useState<string | null>(null);
  const [sheetCreate, setSheetCreate] = useState(false);
  const [sheetDirty, setSheetDirty] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);
  const sheetMember = staff.find((s) => s.id === sheetId) ?? null;
  const sheetOpen = sheetCreate || !!sheetMember;
  const indexInView = filtered.findIndex((s) => s.id === sheetId);
  const openMember = (id: string) => {
    if (sheetOpen && sheetDirty && id !== sheetId) { setPendingSwitch(id); return; }
    setSheetCreate(false);
    setSheetId(id);
  };
  const closeSheet = () => { setSheetId(null); setSheetCreate(false); setServerError(''); };

  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  // Le serveur fait un upsert par email : même route pour créer (invitation
  // envoyée) et corriger (rôle/prénom, sans email). L'AJOUT garde son bouton,
  // c'est lui qui fait partir l'invitation.
  const submit = async (values: StaffFormValues) => {
    if (!selectedEventId) return;
    setSaving(true);
    setServerError('');
    try {
      const res = await apiService.addEventStaff(selectedEventId, values.email.trim(), values.role, values.first_name.trim() || undefined);
      toast.success(res.updated ? t('eventStaff.updated') : t('eventStaff.added'));
      setSheetDirty(false);
      closeSheet();
      refresh();
    } catch (err: any) {
      setServerError(err.response?.data?.detail || t('eventStaff.addError'));
    } finally {
      setSaving(false);
    }
  };

  // Édition : le panneau écrit à chaque champ. Même route, même email (c'est
  // l'identité du juge), le serveur met à jour sans rien envoyer.
  const patchMember = async (patch: StaffPatch) => {
    if (!selectedEventId || !sheetMember) return;
    await apiService.addEventStaff(selectedEventId, sheetMember.email, patch.role ?? sheetMember.role, patch.first_name ?? sheetMember.first_name ?? undefined);
    refresh();
  };

  const [bulkBusy, setBulkBusy] = useState(false);
  const setRoleBulk = async (targets: StaffMember[], role: StaffRole) => {
    if (!selectedEventId) return;
    setBulkBusy(true);
    const failed: string[] = [];
    for (const s of targets) {
      try { await apiService.addEventStaff(selectedEventId, s.email, role, s.first_name || undefined); } catch { failed.push(displayName(s)); }
    }
    setBulkBusy(false);
    setSelection({});
    refresh();
    if (failed.length) toast.error(t('eventStaff.list.failed', { names: failed.join(', ') }));
    else toast.success(t('eventStaff.list.roleUpdated', { count: targets.length, role: roleLabel(role) }));
  };

  const [toRemove, setToRemove] = useState<StaffMember[]>([]);
  const [removing, setRemoving] = useState(false);
  const confirmRemove = async () => {
    if (!selectedEventId) return;
    setRemoving(true);
    const failed: string[] = [];
    for (const s of toRemove) {
      try { await apiService.removeEventStaff(selectedEventId, s.id); } catch { failed.push(displayName(s)); }
    }
    setRemoving(false);
    setToRemove([]);
    setSelection({});
    if (toRemove.some((s) => s.id === sheetId)) closeSheet();
    refresh();
    if (failed.length) toast.error(t('eventStaff.list.failed', { names: failed.join(', ') }));
    else toast.success(toRemove.length === 1 ? t('eventStaff.removed') : t('eventStaff.list.removedMany', { count: toRemove.length }));
  };

  const columns = useMemo<ColumnDef<StaffMember, any>[]>(() => [
    {
      id: 'name',
      accessorFn: (s) => displayName(s),
      header: () => t('eventStaff.firstName'),
      sortingFn: (a, b) => displayName(a.original).localeCompare(displayName(b.original), undefined, { sensitivity: 'base' }),
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="font-semibold text-foreground">{displayName(row.original)}</div>
          {(row.original.first_name || row.original.user_name) && <div className="mt-0.5 truncate text-xs text-muted-foreground">{row.original.email}</div>}
        </div>
      ),
    },
    {
      id: 'role',
      accessorFn: (s) => STAFF_ROLES.indexOf(s.role),
      header: () => t('eventStaff.roleLabel'),
      cell: ({ row }) => <span className={cn(row.original.role === 'judge' ? 'text-muted-foreground' : 'text-foreground')}>{roleLabel(row.original.role)}</span>,
    },
    {
      id: 'account',
      accessorFn: (s) => (s.has_account ? 1 : 0),
      header: () => t('eventStaff.account'),
      cell: ({ row }) => (
        <StatusDot tone={row.original.has_account ? 'success' : 'neutral'} label={row.original.has_account ? t('eventStaff.hasAccount') : t('eventStaff.invited')} className="text-muted-foreground" />
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={t('eventStaff.list.rowActions', { name: displayName(row.original) })}><MoreHorizontal size={16} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => openMember(row.original.id)}><Pencil size={16} className="mr-2" />{t('common.edit')}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setToRemove([row.original])}><Trash2 size={16} className="mr-2" />{t('eventStaff.removeAction')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  if (!selectedEventId) {
    return <div className="p-6 text-muted-foreground">{t('eventStaff.noEvent')}</div>;
  }

  const heads = staff.filter((s) => s.role !== 'judge').length;

  return (
    <div className={cn('space-y-5 p-4 transition-[padding] duration-200 md:p-6', sheetOpen && 'min-[1600px]:pr-[504px]')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{t('eventStaff.intro')}</p>
          {!staffQuery.isLoading && (
            <p className="text-sm text-muted-foreground">
              {[t('eventStaff.list.count', { count: staff.length }), heads ? t('eventStaff.list.headsCount', { count: heads }) : null].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <Button size="sm" onClick={() => { setSheetId(null); setSheetCreate(true); }}><Plus size={16} />{t('eventStaff.newJudge')}</Button>
      </div>

      <Toolbar>
        <SearchField value={query} onChange={setQuery} placeholder={t('eventStaff.list.searchPlaceholder')} />
        <FilterChip
          label={t('eventStaff.roleLabel')}
          selected={roleFilter}
          onChange={setRoleFilter}
          options={STAFF_ROLES.map((r) => ({ value: r, label: roleLabel(r), count: staff.filter((s) => s.role === r).length })).filter((o) => o.count > 0)}
        />
        {(roleFilter.length > 0 || query) && (
          <span className="text-xs text-muted-foreground">{t('eventStaff.list.filteredCount', { shown: filtered.length, total: staff.length })}</span>
        )}
      </Toolbar>

      {staffQuery.isError && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-4">
            {t('eventStaff.loadError')}
            <Button variant="outline" size="sm" onClick={() => staffQuery.refetch()}>{t('common.retry')}</Button>
          </AlertDescription>
        </Alert>
      )}

      <DataTable
        ariaLabel={t('eventStaff.title')}
        columns={columns}
        data={filtered}
        getRowId={(s) => s.id}
        loading={staffQuery.isLoading}
        selectable
        rowSelection={selection}
        onRowSelectionChange={setSelection}
        onRowClick={(s) => openMember(s.id)}
        activeRowId={sheetId}
        initialSorting={[{ id: 'name', desc: false }]}
        empty={staff.length === 0 ? (
          <div className="space-y-4">
            <p>{t('eventStaff.empty')}</p>
            <Button onClick={() => setSheetCreate(true)}><Plus size={16} />{t('eventStaff.newJudge')}</Button>
          </div>
        ) : t('eventStaff.list.noMatch')}
        renderCard={(s, { selected: isSelected, toggle }) => (
          <div className="flex items-start gap-3 py-3">
            <input type="checkbox" checked={isSelected} onChange={toggle} onClick={(e) => e.stopPropagation()} aria-label={t('dataTable.selectRow')} className="mt-1 h-4 w-4 accent-white" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{displayName(s)}</div>
              <div className="truncate text-xs text-muted-foreground">{s.email}</div>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>{roleLabel(s.role)}</span>
                <StatusDot tone={s.has_account ? 'success' : 'neutral'} label={s.has_account ? t('eventStaff.hasAccount') : t('eventStaff.invited')} />
              </div>
            </div>
          </div>
        )}
      />

      <BulkActionBar count={selectedIds.length} onClear={() => setSelection({})}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <BulkAction disabled={bulkBusy}>{t('eventStaff.list.setRole')} ▾</BulkAction>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" side="top">
            {STAFF_ROLES.map((r) => <DropdownMenuItem key={r} onClick={() => setRoleBulk(selected, r)}>{roleLabel(r)}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
        <BulkAction className="text-destructive" onClick={() => setToRemove(selected)}><Trash2 />{t('eventStaff.removeAction')}</BulkAction>
      </BulkActionBar>

      <StaffSheet
        open={sheetOpen}
        member={sheetCreate ? null : sheetMember}
        saving={saving}
        serverError={serverError}
        onSubmit={submit}
        onPatch={patchMember}
        onRemove={() => { if (sheetMember) setToRemove([sheetMember]); }}
        onClose={closeSheet}
        onDirtyChange={setSheetDirty}
        onPrev={indexInView > 0 ? () => setSheetId(filtered[indexInView - 1].id) : undefined}
        onNext={indexInView >= 0 && indexInView < filtered.length - 1 ? () => setSheetId(filtered[indexInView + 1].id) : undefined}
      />

      <ConfirmDialog
        open={pendingSwitch !== null}
        onOpenChange={(o) => { if (!o) setPendingSwitch(null); }}
        title={t('teams.form.discardTitle')}
        message={t('teams.form.discardMessage')}
        confirmLabel={t('teams.form.discardConfirm')}
        destructive
        onConfirm={() => { setSheetDirty(false); setSheetCreate(false); setSheetId(pendingSwitch); setPendingSwitch(null); }}
      />

      <ConfirmDialog
        open={toRemove.length > 0}
        onOpenChange={(o) => { if (!o && !removing) setToRemove([]); }}
        title={t('eventStaff.removeTitle')}
        message={toRemove.length === 1 ? t('eventStaff.removeConfirm', { email: toRemove[0].email }) : t('eventStaff.list.removeManyConfirm', { count: toRemove.length })}
        confirmLabel={t('eventStaff.removeAction')}
        destructive
        loading={removing}
        onConfirm={confirmRemove}
      />
    </div>
  );
};

export default EventStaff;
